import { Button } from "@components/ui/Button"
import { Inset } from "@components/ui/Card"
import { Field, SelectInput, TextInput } from "@components/ui/Field"
import { Segmented } from "@components/ui/Segmented"
import {
  cutCount,
  cutField,
  fieldHandle,
  previewCuts,
  schoolYearBounds,
  withCutCount,
  type FieldError,
  type PeriodDraft,
  type PeriodField,
} from "@lib/evaluationCalendar"
import { formatDate } from "@lib/intl"
import { Trash2 } from "lucide-react"
import { useId } from "react"
import { useTranslation } from "react-i18next"

import { useCutRange } from "./labels"

import {
  CUT_NOUNS,
  MAX_CUTS_PER_PERIOD,
  MAX_PERIOD_LABEL_LENGTH,
  MIN_CUTS_PER_PERIOD,
  type CutNoun,
} from "@/constants/academic"

const CUT_COUNTS = Array.from(
  { length: MAX_CUTS_PER_PERIOD - MIN_CUTS_PER_PERIOD + 1 },
  (_, i) => MIN_CUTS_PER_PERIOD + i,
)

interface Props {
  draft: PeriodDraft
  /** 0-based position. */
  index: number
  /** "Semestre 2" for a position — see `useBlockNames`. */
  blockName: (index: number) => string
  /** "fin del semestre": where the last cut closes. */
  blockEnd: string
  errors: Partial<Record<PeriodField, FieldError>> | undefined
  onChange: (next: PeriodDraft) => void
  onRemove: (key: string) => void
  /** Bounds the date pickers ("2025-2026" → 2025-01-01 … 2026-12-31). */
  schoolYear: string
  disabled?: boolean
}

/**
 * One period of the evaluation calendar (PRD-61 FR-1.1): its name and
 * dates, what its cuts are called and how many, the closing date of each
 * cut but the last, and — as soon as there is a date to show — the cuts as
 * a professor will see them. The preview is the check: a closing date in the
 * wrong month reads wrong at a glance, before any rule has to say so — and
 * its first line carries the years, because the likeliest slip in a period
 * that runs August to January is the year, which "26 sep" cannot show.
 */
export function PeriodFields({
  draft,
  index,
  blockName,
  blockEnd,
  errors,
  onChange,
  onRemove,
  schoolYear,
  disabled,
}: Props) {
  const { t } = useTranslation("common")
  const cutRange = useCutRange()
  const headingId = useId()
  const splitId = useId()
  const name = blockName(index)
  const cuts = cutCount(draft)

  const message = (field: PeriodField) => {
    const error = errors?.[field]
    if (!error) return undefined
    return t(`calendar.error.${error.code}`, {
      other: error.other ? blockName(error.other - 1) : "",
    })
  }
  const set = (patch: Partial<PeriodDraft>) => onChange({ ...draft, ...patch })
  const setCut = (i: number, value: string) =>
    set({ cut_dates: draft.cut_dates.map((d, j) => (j === i ? value : d)) })

  const hasDates = !!(draft.start_date || draft.end_date || draft.cut_dates.some(Boolean))
  const bounds = schoolYearBounds(schoolYear)
  const withYear = (iso: string) =>
    iso ? formatDate(iso, "d MMM yyyy") : t("calendar.preview.no_date")

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 id={headingId} className="text-ink text-sm font-semibold">
          {name}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Trash2 className="h-4 w-4" aria-hidden />}
          aria-label={t("calendar.period.remove_aria", { name })}
          disabled={disabled}
          onClick={() => onRemove(draft.key)}
        >
          {t("calendar.period.remove")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <Field label={t("calendar.period.label")} required error={message("label")}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              data-calendar-field={fieldHandle(draft.key, "label")}
              autoComplete="off"
              maxLength={MAX_PERIOD_LABEL_LENGTH}
              placeholder={t("calendar.period.label_placeholder")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              disabled={disabled}
              value={draft.label}
              onChange={(e) => set({ label: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("calendar.period.start")} required error={message("start_date")}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              data-calendar-field={fieldHandle(draft.key, "start_date")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              disabled={disabled}
              min={bounds?.min}
              max={draft.end_date || bounds?.max}
              value={draft.start_date}
              onChange={(e) => set({ start_date: e.target.value })}
            />
          )}
        </Field>
        <Field label={t("calendar.period.end")} required error={message("end_date")}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              data-calendar-field={fieldHandle(draft.key, "end_date")}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              disabled={disabled}
              min={draft.start_date || bounds?.min}
              max={bounds?.max}
              value={draft.end_date}
              onChange={(e) => set({ end_date: e.target.value })}
            />
          )}
        </Field>
      </div>

      {/* One decision, read as one line: "Se divide en [3] [Parciales]". */}
      <div role="group" aria-labelledby={splitId} className="flex flex-col gap-1.5">
        <span id={splitId} className="text-ink text-sm font-medium">
          {t("calendar.period.noun")}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <SelectInput
            aria-label={t(`calendar.cut_count.${draft.noun}`)}
            className="w-20 shrink-0"
            disabled={disabled}
            value={String(cuts)}
            onChange={(e) => onChange(withCutCount(draft, Number(e.target.value)))}
          >
            {CUT_COUNTS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </SelectInput>
          <Segmented<CutNoun>
            ariaLabel={t("calendar.period.noun_kind")}
            value={draft.noun}
            onChange={(noun) => !disabled && set({ noun })}
            options={CUT_NOUNS.map((noun) => ({
              value: noun,
              label: t(`calendar.noun_plural.${noun}`),
              disabled,
            }))}
          />
        </div>
      </div>

      {/* Every cut has its slot, so "3" shows three: the last is not typed —
          it closes when the block ends — and says so where its field would be. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {draft.cut_dates.map((value, i) => (
          <Field
            key={cutField(i)}
            label={t(`calendar.closes.cut.${draft.noun}`, { number: i + 1 })}
            required
            error={message(cutField(i))}
          >
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                type="date"
                data-calendar-field={fieldHandle(draft.key, cutField(i))}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                disabled={disabled}
                min={draft.cut_dates[i - 1] || draft.start_date || bounds?.min}
                max={draft.end_date || bounds?.max}
                value={value}
                onChange={(e) => setCut(i, e.target.value)}
              />
            )}
          </Field>
        ))}
        <div className="flex flex-col gap-1.5">
          <span className="text-ink text-sm font-medium">
            {t(`calendar.closes.cut.${draft.noun}`, { number: cuts })}
          </span>
          <p
            className="text-ink-muted flex min-h-11 items-center text-base md:min-h-9 md:text-sm"
            data-numeric
          >
            {draft.end_date
              ? t("calendar.closes.last_value", {
                  date: withYear(draft.end_date),
                  end: blockEnd,
                })
              : t("calendar.closes.last_pending", { end: blockEnd })}
          </p>
        </div>
      </div>

      {hasDates && (
        <Inset className="px-3 py-2.5">
          <p className="text-label">{t("calendar.preview.title")}</p>
          <p className="text-ink mt-1 text-sm font-medium" data-numeric>
            {t("calendar.preview.span", {
              start: withYear(draft.start_date),
              end: withYear(draft.end_date),
            })}
          </p>
          <ol className="text-ink mt-1 space-y-0.5 text-sm" data-numeric>
            {previewCuts(draft).map((c) => (
              <li key={c.number}>{cutRange(draft.noun, c.number, c.start, c.end)}</li>
            ))}
          </ol>
        </Inset>
      )}
    </section>
  )
}
